import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  List, 
  ListItem,
  Radio, 
  FormControlLabel,
  Divider,
  Chip
} from "@mui/material";
import { useOnboarding } from "context/OnboardingContext";
import { prefillApi } from "services/onboarding/prefillApi";

const PrefillConflictResolver = ({ open, onClose, sessionId }) => {
  const { sessionToken, sessionId: contextSessionId } = useOnboarding();
  const [draft, setDraft] = useState(null);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadDraft();
    }
  }, [open]);

  const loadDraft = async () => {
    setLoading(true);
    try {
      const data = await prefillApi.getDraft({ sessionId: contextSessionId, sessionToken });
      setDraft(data);
    } catch (err) {
      console.error("Failed to load prefill draft", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      // Extract candidate IDs from selections
      const candidateIds = [];
      const overrides = {};

      Object.entries(draft.steps).forEach(([step, candidates]) => {
        candidates.forEach(c => {
          if (selections[c.candidate_id]) {
            candidateIds.push(c.candidate_id);
            // If the user provided an override value in the UI (not implemented here yet)
            // overrides[c.candidate_id] = ...
          }
        });
      });

      if (candidateIds.length === 0) throw new Error("No candidates selected");

      await prefillApi.applyDraft({ 
        sessionId: contextSessionId, 
        sessionToken, 
        candidateIds, 
        overrides 
      });
      
      onClose();
    } catch (err) {
      alert(err.message || "Failed to apply draft");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !draft) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent sx={{ py: 5, textAlign: 'center' }}>
          <Typography>Loading suggestions...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
        Review Document Suggestions
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" mb={3}>
          We found several values in your uploaded documents. Please select the correct ones to prefill your onboarding.
        </Typography>

        {Object.entries(draft?.steps || {}).map(([step, candidates]) => (
          <Box key={step} mb={4}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={1} sx={{ textTransform: 'capitalize' }}>
              {step.replace('_', ' ')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {candidates.map((candidate, idx) => (
                <ListItem 
                  key={candidate.candidate_id} 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: '12px', 
                    mb: 1,
                    bgcolor: selections[candidate.candidate_id] ? 'action.hover' : 'transparent'
                  }}
                >
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <Box display="flex" alignItems="center" mb={1} ml={0}>
                      <FormControlLabel
                        value={candidate.candidate_id}
                        control={
                          <Radio 
                            checked={!!selections[candidate.candidate_id]} 
                            onChange={() => setSelections(prev => ({ 
                              ...prev, 
                              [candidate.candidate_id]: true 
                            }))} 
                          />
                        }
                        label={
                          <Typography variant="body1" fontWeight="medium">
                            {candidate.normalized_value_json}
                          </Typography>
                        }
                      />
                      <Chip 
                        label={`${Math.round(candidate.confidence * 100)}% Confidence`} 
                        size="small" 
                        color={candidate.confidence > 0.9 ? "success" : "warning"}
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary" ml={1}>
                      Source: {candidate.document_id ? `Doc #${candidate.document_id}` : 'Direct Import'} 
                      {candidate.source_locator_json?.excerpt && ` — "${candidate.source_locator_json.excerpt}"`}
                    </Typography>
                  </Box>
                  </ListItem>
                ))}
            </List>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button 
          onClick={handleApply} 
          variant="contained"
          color="text" 
          disabled={loading || Object.keys(selections).length === 0}
        >
          {loading ? "Applying..." : "Apply Selected Suggestions"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrefillConflictResolver;
